/**
 * Intercompany Engine v2 — 16 blokkade-controles.
 *
 * Pure utils zonder Prisma-imports. Caller (zie
 * `apps/api/src/services/intercompany-period-berekening.ts`) verzamelt context
 * uit Prisma en geeft normaale objecten door.
 *
 * Spec: docs/PLAN-IC-ENGINE-EN-ONBOARDING-V2.md §8 (16 blokkade-controles).
 *
 * De 16 controles:
 *   1. geenGeldigeRule         — geen actieve rule voor (bron, gebruikende, type, datum)
 *   2. bvKoppelingOntbreekt    — bron- of gebruikende-BV bestaat niet
 *   3. bedragWijktAf           — totaal wijkt >25% af van vergelijkbare tx 3 maanden terug
 *   4. negatiefBedrag          — totaal < 0 (alleen toegestaan voor type=CORRECTIE)
 *   5. nulBedrag               — totaal = 0 zonder handmatige opslag-override
 *   6. urenZonderGoedkeuring   — type=MEDEWERKER, dayEntries niet allemaal goedgekeurd
 *   7. voertuigZonderEigenaar  — type=AUTO, voertuig.eigenaarBvId is null
 *   8. voertuigZonderToewijzing — type=AUTO, geen actieve VoertuigToewijzing
 *   9. tankpasZonderTransacties — type=TANKPAS, geen FuelCardTransactions in periode
 *  10. feeZonderVerdeelsleutelTotaal — VERDEELSLEUTEL-methode, sleutel ≠ 100±0.01
 *  11. feeZonderGrondslag      — PERCENTAGE_OMZET/PERCENTAGE_KOSTEN zonder grondslag
 *  12. geblokkeerdeBron        — bron-BV `actief=false`
 *  13. geblokkeerdeGebruikende — gebruikende-BV `actief=false`
 *  14. dubbelePost             — andere tx in dezelfde periode/rule met overlap
 *  15. verkeerdeBtw            — btwPercentage afwezig of mismatch met Entity.btwPlichtig
 *  16. historieMismatch        — type=MEDEWERKER, EmployeeCompanyHistory toont andere BV
 */

import type { ICRuleType, ICFeeMethode } from '@streep/types'

// ─── Context-typen ─────────────────────────────────────────────────────────

export interface BlokkadeRule {
  id: string
  type: ICRuleType
  bronBvId: string
  gebruikendeBvId: string
  geldigVanaf: Date
  geldigTot: Date | null
  actief: boolean
}

export interface BlokkadeBv {
  id: string
  actief: boolean
  btwPlichtig: boolean
}

export interface BlokkadeWerkbonDayEntry {
  id: string
  employeeId: string
  date: Date
  hoursApproved: boolean // true als werkbon-dayentry.status="GOEDGEKEURD"
}

export interface BlokkadeVoertuig {
  id: string
  eigenaarBvId: string | null
}

export interface BlokkadeVoertuigToewijzing {
  id: string
  voertuigId: string
  startDatum: Date
  eindDatum: Date | null
}

export interface BlokkadeFuelCardTransaction {
  id: string
  fuelCardId: string
  datum: Date
  bedragInclBtw: number
}

export interface BlokkadeFeeRule {
  methode: ICFeeMethode
  verdeelsleutel: Record<string, number> | null
  vastBedrag: number | null
  percentage: number | null
}

export interface BlokkadeEmployeeCompanyHistory {
  bvId: string
  startDatum: Date
  eindDatum: Date | null
}

export interface BlokkadeReferenceTx {
  id: string
  totaal: number
  /** ISO-string van periode-start (1e van de maand) — gebruikt voor sortering. */
  periodeStart: Date
  bronBvId: string
  gebruikendeBvId: string
  type: ICRuleType
  ruleId: string | null
}

/**
 * Het hele transactie-object zoals de blokkade-controles het zien. Niet 1:1
 * Prisma-model maar de relevante velden.
 */
export interface BlokkadeTx {
  id?: string
  type: ICRuleType
  bronBvId: string
  gebruikendeBvId: string
  ruleId: string | null
  /** 1e van de maand van de periode (UTC). */
  periodeStart: Date
  totaal: number
  subtotaal: number
  opslag: number
  btwPercentage: number | null
  /** Bij CORRECTIE; toegestaan negatief. */
  origineelTxId: string | null
}

/**
 * Alles wat de caller verzamelt om de 16 controles uit te voeren.
 * Velden die niet relevant zijn voor het type van de transactie mogen
 * leeg gelaten worden.
 */
export interface BlokkadeContext {
  /** Voor controle 1, 6 (bij type=MEDEWERKER ook). */
  rule: BlokkadeRule | null

  /** Voor controle 2, 12, 13, 15. */
  bronBv: BlokkadeBv | null
  gebruikendeBv: BlokkadeBv | null

  /** Voor controle 3 — vergelijkbare tx van 3 maanden terug. */
  referenceTx?: BlokkadeReferenceTx | null

  /** Voor controle 5 — was er een handmatige opslag-override door operator? */
  hasManualOpslagOverride?: boolean

  /** Voor controle 6 — werkbon dayEntries die in deze tx zijn meegenomen. */
  werkbonDayEntries?: BlokkadeWerkbonDayEntry[]

  /** Voor controle 7. */
  voertuig?: BlokkadeVoertuig | null

  /** Voor controle 8 — actieve toewijzingen overlappend met periode. */
  voertuigToewijzingen?: BlokkadeVoertuigToewijzing[]

  /** Voor controle 9 — tankpas-transacties binnen de periode. */
  fuelCardTransactions?: BlokkadeFuelCardTransaction[]

  /** Voor controle 10, 11. */
  feeRule?: BlokkadeFeeRule | null
  /** Voor controle 11 — extra grondslagen (omzet/kosten) als bron-data binnenkomt. */
  inputGrondslagen?: { omzet: number | null; kosten: number | null }

  /** Voor controle 14 — andere tx in dezelfde (periode, rule) die overlap kunnen
   *  veroorzaken. ID van de huidige tx zelf hoort hier dus *niet* in. */
  zelfdePeriodeAndereTransacties?: Array<{
    id: string
    ruleId: string | null
    bronBvId: string
    gebruikendeBvId: string
    type: ICRuleType
    /** Set van werkbon-dayEntry-ids die in die andere tx zitten — als overlap met
     *  huidige werkbonDayEntries dan dubbel-post. */
    werkbonDayEntryIds?: string[]
  }>

  /** Voor controle 16 — historie-records voor de medewerker, geordend nieuwste eerst. */
  employeeCompanyHistory?: BlokkadeEmployeeCompanyHistory[]
}

export interface BlokkadeResultaat {
  blocked: boolean
  reasons: string[]
  /** Detail per controle — kunnen we in tx.blokkadeDetails bewaren. */
  details: Record<string, unknown>
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function within(d: Date, vanaf: Date, tot: Date | null): boolean {
  if (d.getTime() < vanaf.getTime()) return false
  if (tot && d.getTime() > tot.getTime()) return false
  return true
}

function sumPctApprox100(verdeelsleutel: Record<string, number>): boolean {
  const sum = Object.values(verdeelsleutel).reduce((a, b) => a + b, 0)
  return Math.abs(sum - 100) <= 0.01
}

function isVerdeelsleutelMethode(methode: ICFeeMethode): boolean {
  return (
    methode === 'VERDEELSLEUTEL_FTE' ||
    methode === 'VERDEELSLEUTEL_UREN' ||
    methode === 'VERDEELSLEUTEL_OMZET'
  )
}

// ─── Individuele controles ─────────────────────────────────────────────────

export function geenGeldigeRule(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  // CORRECTIE-tx en handmatige VASTE_KOSTEN mogen zonder rule worden ingevoerd.
  if (tx.type === 'CORRECTIE' || tx.type === 'VASTE_KOSTEN') return false
  if (!ctx.rule) return true
  if (!ctx.rule.actief) return true
  if (ctx.rule.bronBvId !== tx.bronBvId) return true
  if (ctx.rule.gebruikendeBvId !== tx.gebruikendeBvId) return true
  if (ctx.rule.type !== tx.type) return true
  if (!within(tx.periodeStart, ctx.rule.geldigVanaf, ctx.rule.geldigTot)) return true
  return false
}

export function bvKoppelingOntbreekt(_tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  return !ctx.bronBv || !ctx.gebruikendeBv
}

export function bedragWijktAf(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  const ref = ctx.referenceTx
  if (!ref) return false
  if (ref.totaal === 0) return false
  // Negatieve correctie-totaal heeft geen zin als afwijkingscheck.
  if (tx.type === 'CORRECTIE') return false
  const afwijking = Math.abs(tx.totaal - ref.totaal) / Math.abs(ref.totaal)
  return afwijking > 0.25
}

export function negatiefBedrag(tx: BlokkadeTx): boolean {
  if (tx.totaal >= 0) return false
  return tx.type !== 'CORRECTIE'
}

export function nulBedrag(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  if (tx.totaal !== 0) return false
  // CORRECTIE en handmatige VASTE_KOSTEN met handmatige opslag mogen 0 zijn.
  if (tx.type === 'CORRECTIE') return false
  if (ctx.hasManualOpslagOverride) return false
  return true
}

export function urenZonderGoedkeuring(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  if (tx.type !== 'MEDEWERKER') return false
  const entries = ctx.werkbonDayEntries ?? []
  if (entries.length === 0) return true
  return entries.some((e) => !e.hoursApproved)
}

export function voertuigZonderEigenaar(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  if (tx.type !== 'AUTO') return false
  if (!ctx.voertuig) return true
  return ctx.voertuig.eigenaarBvId == null
}

export function voertuigZonderToewijzing(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  if (tx.type !== 'AUTO') return false
  const toewijzingen = ctx.voertuigToewijzingen ?? []
  if (toewijzingen.length === 0) return true
  // Periode-grenzen: 1e van de maand t/m laatste van de maand.
  const j = tx.periodeStart.getUTCFullYear()
  const m = tx.periodeStart.getUTCMonth()
  const periodStart = new Date(Date.UTC(j, m, 1))
  const periodEnd = new Date(Date.UTC(j, m + 1, 0, 23, 59, 59))
  const overlap = toewijzingen.some((t) => {
    const eind = t.eindDatum ?? new Date(Date.UTC(2999, 0, 1))
    return t.startDatum.getTime() <= periodEnd.getTime() && eind.getTime() >= periodStart.getTime()
  })
  return !overlap
}

export function tankpasZonderTransacties(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  if (tx.type !== 'TANKPAS') return false
  return (ctx.fuelCardTransactions ?? []).length === 0
}

export function feeZonderVerdeelsleutelTotaal(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  if (tx.type !== 'MANAGEMENT_FEE') return false
  const fee = ctx.feeRule
  if (!fee) return false
  if (!isVerdeelsleutelMethode(fee.methode)) return false
  if (!fee.verdeelsleutel || Object.keys(fee.verdeelsleutel).length === 0) return true
  return !sumPctApprox100(fee.verdeelsleutel)
}

export function feeZonderGrondslag(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  if (tx.type !== 'MANAGEMENT_FEE') return false
  const fee = ctx.feeRule
  if (!fee) return false
  const grondslagen = ctx.inputGrondslagen ?? { omzet: null, kosten: null }
  if (fee.methode === 'PERCENTAGE_OMZET') return grondslagen.omzet == null
  if (fee.methode === 'PERCENTAGE_KOSTEN') return grondslagen.kosten == null
  return false
}

export function geblokkeerdeBron(_tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  if (!ctx.bronBv) return false // dat is controle 2
  return !ctx.bronBv.actief
}

export function geblokkeerdeGebruikende(_tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  if (!ctx.gebruikendeBv) return false // dat is controle 2
  return !ctx.gebruikendeBv.actief
}

export function dubbelePost(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  const others = ctx.zelfdePeriodeAndereTransacties ?? []
  if (others.length === 0) return false
  // Voor MEDEWERKER-tx: overlap op werkbon-dayEntry-ids = dubbel.
  if (tx.type === 'MEDEWERKER') {
    const huidigeDayEntryIds = new Set((ctx.werkbonDayEntries ?? []).map((d) => d.id))
    if (huidigeDayEntryIds.size === 0) return false
    return others.some((o) => {
      if (o.type !== 'MEDEWERKER') return false
      return (o.werkbonDayEntryIds ?? []).some((id) => huidigeDayEntryIds.has(id))
    })
  }
  // Voor andere types: zelfde rule + zelfde bron+gebruikende+type binnen de
  // periode is een dubbel (excl. correctie-keten — die heeft eigen origineelTxId).
  if (tx.type === 'CORRECTIE') return false
  return others.some(
    (o) =>
      o.type === tx.type &&
      o.ruleId === tx.ruleId &&
      o.bronBvId === tx.bronBvId &&
      o.gebruikendeBvId === tx.gebruikendeBvId,
  )
}

export function verkeerdeBtw(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  // Btw moet aanwezig zijn als beide entiteiten btw-plichtig zijn.
  // Als één van beide niet btw-plichtig is moet btwPercentage 0 zijn.
  if (!ctx.bronBv || !ctx.gebruikendeBv) return false // dat is controle 2
  if (tx.btwPercentage == null) return true
  const btwShouldBeZero = !ctx.bronBv.btwPlichtig || !ctx.gebruikendeBv.btwPlichtig
  if (btwShouldBeZero && tx.btwPercentage !== 0) return true
  if (!btwShouldBeZero && tx.btwPercentage === 0) return true
  return false
}

export function historieMismatch(tx: BlokkadeTx, ctx: BlokkadeContext): boolean {
  if (tx.type !== 'MEDEWERKER') return false
  const dayEntries = ctx.werkbonDayEntries ?? []
  const history = ctx.employeeCompanyHistory ?? []
  if (dayEntries.length === 0 || history.length === 0) return false
  // Voor elke dayEntry: pak het EmployeeCompanyHistory-record waar
  // startDatum <= date < (eindDatum ?? +∞). Als bvId daarvan niet
  // overeenkomt met tx.bronBvId, mismatch.
  return dayEntries.some((e) => {
    const matching = history.find((h) => {
      const eind = h.eindDatum ?? new Date(Date.UTC(2999, 0, 1))
      return h.startDatum.getTime() <= e.date.getTime() && e.date.getTime() < eind.getTime()
    })
    if (!matching) return true
    return matching.bvId !== tx.bronBvId
  })
}

// ─── Orchestrator ──────────────────────────────────────────────────────────

interface BlokkadeCheck {
  key: string
  fn: (tx: BlokkadeTx, ctx: BlokkadeContext) => boolean
  reason: string
}

const CHECKS: BlokkadeCheck[] = [
  { key: 'geenGeldigeRule', fn: geenGeldigeRule, reason: 'Geen geldige IC-regel voor (bron, gebruikende, type, datum)' },
  { key: 'bvKoppelingOntbreekt', fn: bvKoppelingOntbreekt, reason: 'Bron- of gebruikende-BV ontbreekt' },
  { key: 'bedragWijktAf', fn: bedragWijktAf, reason: 'Totaal wijkt meer dan 25% af van vergelijkbare transactie 3 maanden terug' },
  { key: 'negatiefBedrag', fn: negatiefBedrag, reason: 'Totaal is negatief (alleen toegestaan bij CORRECTIE)' },
  { key: 'nulBedrag', fn: nulBedrag, reason: 'Totaal is nul zonder handmatige opslag-override' },
  { key: 'urenZonderGoedkeuring', fn: urenZonderGoedkeuring, reason: 'Werkbon-uren zijn nog niet allemaal goedgekeurd' },
  { key: 'voertuigZonderEigenaar', fn: voertuigZonderEigenaar, reason: 'Voertuig heeft geen eigenaar-BV' },
  { key: 'voertuigZonderToewijzing', fn: voertuigZonderToewijzing, reason: 'Geen actieve voertuig-toewijzing in deze periode' },
  { key: 'tankpasZonderTransacties', fn: tankpasZonderTransacties, reason: 'Geen tankpas-transacties in deze periode' },
  { key: 'feeZonderVerdeelsleutelTotaal', fn: feeZonderVerdeelsleutelTotaal, reason: 'Verdeelsleutel-percentages tellen niet op tot 100' },
  { key: 'feeZonderGrondslag', fn: feeZonderGrondslag, reason: 'Management-fee mist grondslag (omzet/kosten)' },
  { key: 'geblokkeerdeBron', fn: geblokkeerdeBron, reason: 'Bron-BV is gedeactiveerd (actief=false)' },
  { key: 'geblokkeerdeGebruikende', fn: geblokkeerdeGebruikende, reason: 'Gebruikende-BV is gedeactiveerd (actief=false)' },
  { key: 'dubbelePost', fn: dubbelePost, reason: 'Dubbele post - andere transactie in dezelfde periode dekt al dezelfde data' },
  { key: 'verkeerdeBtw', fn: verkeerdeBtw, reason: 'Btw-percentage ontbreekt of klopt niet met btw-plichtigheid van entiteit' },
  { key: 'historieMismatch', fn: historieMismatch, reason: 'Werkbon-dag valt buiten de BV-historie van de medewerker' },
]

/**
 * Voer alle 16 controles uit op een transactie + context.
 * Geblokkeerd zodra ten minste één controle aanslaat.
 */
export function evaluerenBlokkades(tx: BlokkadeTx, ctx: BlokkadeContext): BlokkadeResultaat {
  const reasons: string[] = []
  const details: Record<string, unknown> = {}
  for (const check of CHECKS) {
    const triggered = check.fn(tx, ctx)
    details[check.key] = triggered
    if (triggered) {
      reasons.push(`[${check.key}] ${check.reason}`)
    }
  }
  return {
    blocked: reasons.length > 0,
    reasons,
    details,
  }
}

export const ALL_BLOKKADE_KEYS = CHECKS.map((c) => c.key) as readonly string[]
